import { RouteName } from "constant/routeNames";
import LazyLoading from "pages/videoPage/shared/LazyLoading";
import React from "react";
import { FaCrown, FaPlay } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { setVideoStart, setVideoSuccess } from "redux/VideoPlayerRedux";
import AddToFavCreator from "./AddToFavCreator";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { ContentType } from "services/subscription/types";
import { usePremiumAccess } from "hooks/usePremiumAccess";
import PremiumContentModal from "components/PremiumContentModal";

interface PackageResponse {
  id: number;
  entitlement_id: string;
  title: string;
  price: string;
  thumbnail: string;
  created_at: string;
  description: string;
  is_subscribed: boolean;
  role_id: number;
  color?: string;
  slug: string;
}

function CreatorVideoCard({ video, slug, id, season }: any) {
  const creatorSlug = slug || video?.creator_slug || video?.creator?.slug;
  const creatorName =
    video?.creator_name ||
    video?.creator_artist?.name ||
    video?.creator?.name;
  const { isModalOpen, setIsModalOpen, checkAccess } = usePremiumAccess();

  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const loggedInUser = useSelector((state: any) => state?.user?.currentUser);
  const creatorVideoWatchProgress = useSelector(
    (state: any) => state?.video?.creatorVideoWatchProgress
  );

  // YouTube-style watch progress for this card's video.
  const watchProgressEntry = creatorVideoWatchProgress?.[video?.id];
  const watchProgressPercent =
    watchProgressEntry?.duration > 0
      ? Math.min(
          100,
          (watchProgressEntry.currentTime / watchProgressEntry.duration) * 100
        )
      : 0;

  const episodeOnClick = async () => {
    if (!creatorSlug) {
      return;
    }

    const access = await checkAccess({
      contentType: ContentType.CREATOR_VIDEOS,
      id: video?.id,
      isPremiumContent,
      isCreator: true,
    });
    if (!access) return;

    setTimeout(async () => {
      const value = {
        ...video,
      };

      navigate(
        RouteName.CreatorPlaylistRoute.replace(":slug", creatorSlug)
          .replace(":id", video?.season_id || video?.playlist_id || video?.id)
          .replace(":stream", video?.stream_key),
      );

      dispatch(setVideoStart());
      dispatch(setVideoSuccess(value));
    }, 0);
  };

  const isPremiumContent = video?.plan_id === 2;

  const packageObject: PackageResponse = {
    id: video?.service_id,
    entitlement_id: video?.entitlement_id,
    title: video?.name,
    price: video?.price,
    thumbnail: video?.thumbnail_url,
    created_at: video?.created_at || "",
    description: video?.description,
    is_subscribed: video?.is_Subscribed,
    role_id: video?.plan_id,
    slug: video?.slug,
  };

  return (
    <>
      <PremiumContentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        isCreator={true}
        packageObject={packageObject}
      />

      <div className="group mb-[10px] block w-full cursor-pointer rounded-2xl px-2 py-2 transition-all duration-300 ease-out hover:-translate-y-1 hover:bg-slate-800 hover:shadow-2xl hover:shadow-[var(--red)]/10">
        {/* image */}
        <div className="bg-img relative aspect-video w-full overflow-hidden rounded-xl ring-1 ring-white/5 transition-all duration-300 group-hover:ring-2 group-hover:ring-[var(--red)]/50 max-sm:mx-auto max-sm:w-[98%]">
          {/* Premium badge (glassy) */}
          {isPremiumContent && (
            <div className="absolute left-2.5 top-2.5 z-20 flex items-center gap-1 rounded-full border border-white/15 bg-black/40 px-2 py-1 text-[10px] font-semibold text-white backdrop-blur-md">
              <FaCrown className="text-[var(--red)] text-[10px]" />
              <span>PREMIUM</span>
            </div>
          )}

          <LazyLoading
            imgSrc={video?.thumbnail_url}
            imgAlt={"Card pic"}
            imgHeight={"100%"}
            imgWidth={"100%"}
            wrapperProps={{
              style: {
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                display: "block",
              },
            }}
            className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
            }}
          />

          {/* play overlay — revealed on hover via .bg-img:hover .play-button */}
          <div className="play-button absolute inset-0 !z-50 flex items-center justify-center rounded-xl bg-gradient-to-t from-black/70 to-transparent">
            <button
              className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--red)] text-white shadow-xl transition-transform duration-300 hover:scale-110 active:scale-95"
              onClick={episodeOnClick}
            >
              <FaPlay className="translate-x-[1px]" />
            </button>

            <div className="absolute bottom-3 flex w-[92%] items-center justify-between">
              <p className="rounded-md bg-black/50 px-2 py-0.5 text-[12px] font-medium text-white backdrop-blur-sm">
                {video?.runtime === 1 ? "1:00 min" : `${video?.runtime} min`}
              </p>
              <AddToFavCreator
                loggedInUser={loggedInUser}
                video={video}
                icon={true}
              />
            </div>
          </div>

          {/* YouTube-style watch progress bar, pinned to the bottom edge of the thumbnail */}
          {watchProgressPercent > 0 && (
            <div className="absolute bottom-0 left-0 z-20 h-[3px] w-full bg-white/20">
              <div
                className="h-full bg-[var(--red)]"
                style={{ width: `${watchProgressPercent}%` }}
              />
            </div>
          )}
        </div>
        <div className="relative mt-3 mb-2 min-h-[60px]">
          <div className="ml-1">
            <h2
              className="mb-1 w-[90%] cursor-pointer text-[14px] font-bold text-slate-200 transition-colors group-hover:text-white"
              // This project's Tailwind (3.2.x) doesn't ship `line-clamp-*`
              // natively and the plugin isn't installed, so `line-clamp-2`
              // silently did nothing here - setting the real CSS directly.
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
              data-tooltip-id={
                video?.title?.length > 50
                  ? `add-song-to-favorite-${video?.title}`
                  : ""
              }
            >
              {video?.title}
            </h2>

            {creatorName && (
              <p
                className="w-[90%] text-[12px] font-medium text-slate-400"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {creatorName}
              </p>
            )}

            <div className="z-50 text-sm">
              <ReactTooltip
                place="top"
                id={`add-song-to-favorite-${video?.title}`}
                content={video?.title}
                style={{ width: "180px" }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default CreatorVideoCard;
